import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react'; // React için modern ikonlar
import axios from 'axios';

// API'nin temel URL'sini belirtiyoruz
// Dilerseniz bunu .env dosyasına taşıyabilirsiniz
const API_URL = process.env.REACT_APP_API_URL;

const BackendModalGallery = ({ placeName }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(null);
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Mekan adı değiştiğinde görselleri yeniden çekiyoruz
    useEffect(() => {
        if (placeName) {
            fetchImages();
        }
    }, [placeName]);

    // Görselleri backend API'den çeken asenkron fonksiyon
    const fetchImages = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_URL}/api/images/${placeName}`);
            setImages(response.data);
        } catch (err) {
            console.error("Görsel yükleme hatası:", err);
            setError("Görseller yüklenemedi. Lütfen tekrar deneyin.");
        } finally {
            setLoading(false);
        }
    };

    // Galeri modalını açar
    const openModal = () => {
        setIsModalOpen(true);
    };

    // Galeri modalını kapatır
    const closeModal = () => {
        setIsModalOpen(false);
    };

    // Lightbox'ı açar ve seçili görseli ayarlar
    const openLightbox = (index) => {
        setSelectedImageIndex(index);
    };

    // Lightbox'ı kapatır
    const closeLightbox = () => {
        setSelectedImageIndex(null);
    };

    // Lightbox'taki görseller arasında gezinme
    const navigateImages = (direction) => {
        let nextIndex;
        if (direction === 'prev') {
            nextIndex = (selectedImageIndex - 1 + images.length) % images.length;
        } else {
            nextIndex = (selectedImageIndex + 1) % images.length;
        }
        setSelectedImageIndex(nextIndex);
    };

    return (
        <div className="relative z-20">
            {/* Galeriyi açan buton */}
            <button
                type='button'
                className="px-6 py-3 bg-indigo-600 text-white rounded-full font-semibold shadow-lg hover:bg-indigo-700 transition-colors duration-300 transform hover:-translate-y-1"
                onClick={openModal}
            >
                📸 Daha Fazla Görsel
            </button>

            {/* Ana Galeri Modalı */}
            {isModalOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-75 backdrop-blur-sm transition-opacity duration-300"
                    onClick={closeModal}
                >
                    <div
                        className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-gray-900 rounded-lg shadow-2xl p-6 transform transition-all duration-300 scale-95 md:scale-100"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal kapatma butonu */}
                        <button
                            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors duration-200"
                            onClick={closeModal}
                        >
                            <X size={28} />
                        </button>
                        
                        {/* Modal başlığı */}
                        <h3 className="text-2xl font-bold text-amber-400 mb-6 text-center">
                            {placeName} Görselleri
                        </h3>

                        {/* Yükleme animasyonu */}
                        {loading && (
                            <div className="flex justify-center items-center h-40">
                                <div className="w-12 h-12 border-4 border-gray-600 border-t-indigo-400 rounded-full animate-spin"></div>
                            </div>
                        )}
                        
                        {/* Hata mesajı */}
                        {error && (
                            <p className="text-center text-red-500 font-medium my-4">{error}</p>
                        )}
                        
                        {/* Galeri içeriği */}
                        {!loading && !error && (
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                {images.length > 0 ? (
                                    images.map((imgSrc, index) => (
                                        <div
                                            key={index}
                                            className="relative w-full h-40 overflow-hidden rounded-lg shadow-md group cursor-pointer"
                                            onClick={() => openLightbox(index)}
                                        >
                                            <img
                                                src={imgSrc}
                                                alt={`Mekan görseli ${index + 1}`}
                                                className="w-full h-full object-cover transform transition-transform duration-300 group-hover:scale-110"
                                            />
                                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity duration-300"></div>
                                        </div>
                                    ))
                                ) : (
                                    <p className="col-span-full text-center text-gray-400 text-lg">Bu mekan için görsel bulunamadı.</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Lightbox (Tam Ekran Görsel Gösterimi) */}
            {selectedImageIndex !== null && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black bg-opacity-90 backdrop-blur-sm transition-opacity duration-300"
                    onClick={closeLightbox}
                >
                    {/* Lightbox kapatma butonu */}
                    <button
                        className="absolute top-4 right-4 text-gray-300 hover:text-white transition-colors duration-200"
                        onClick={closeLightbox}
                    >
                        <X size={32} />
                    </button>

                    {/* Navigasyon butonları */}
                    {images.length > 1 && (
                        <>
                            <button
                                type="button"
                                className="absolute left-4 p-2 md:p-3 text-white bg-black bg-opacity-50 rounded-full hover:bg-opacity-80 transition-colors duration-200 z-10"
                                onClick={(e) => { 
                                    e.preventDefault(); 
                                    e.stopPropagation(); 
                                    navigateImages('prev'); 
                                }}
                            >
                                <ChevronLeft size={32} />
                            </button>
                            <button
                                type="button"
                                className="absolute right-4 p-2 md:p-3 text-white bg-black bg-opacity-50 rounded-full hover:bg-opacity-80 transition-colors duration-200 z-10"
                                onClick={(e) => { 
                                    e.preventDefault(); 
                                    e.stopPropagation(); 
                                    navigateImages('next'); 
                                }}
                            >
                                <ChevronRight size={32} />
                            </button>
                        </>
                    )}

                    {/* Görsel içeriği */}
                    <div className="relative max-w-full max-h-full">
                        <img 
                            src={images[selectedImageIndex]} 
                            alt={`Mekan görseli ${selectedImageIndex + 1}`} 
                            className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" 
                        />
                        <p className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white text-sm px-4 py-1 rounded-full">
                            {selectedImageIndex + 1} / {images.length}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BackendModalGallery;